import React, { useEffect, useRef } from 'react';

interface Point {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

interface Triangle {
  p1: Point;
  p2: Point;
  p3: Point;
  color: string;
  alpha: number;
}

const AnimatedTrianglesBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const pointsRef = useRef<Point[]>([]);
  const trianglesRef = useRef<Triangle[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Установка размера canvas
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Параметры
    const pointCount = 100;
    const connectionDistance = 180;
    const pointSpeed = 0.3;
    const pointRadius = 1.5;

    // Инициализация точек
    const initPoints = (): Point[] => {
      const points: Point[] = [];
      for (let i = 0; i < pointCount; i++) {
        points.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * pointSpeed,
          vy: (Math.random() - 0.5) * pointSpeed,
        });
      }
      return points;
    };

    pointsRef.current = initPoints();

    // Функция для вычисления расстояния между двумя точками
    const distance = (p1: Point, p2: Point): number => {
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      return Math.sqrt(dx * dx + dy * dy);
    };

    // Функция для получения RGB цвета на основе позиции точки (без альфа-канала)
    // Для заливки - приглушенные темные цвета
    const getRGBForPoint = (x: number, y: number): { r: number; g: number; b: number } => {
      // Градиент от синего (внизу слева) к белому (вверху справа)
      const normalizedX = x / canvas.width;
      const normalizedY = y / canvas.height;
      
      // Комбинируем позицию для создания диагонального градиента
      const gradient = normalizedX * 0.5 + normalizedY * 0.5;
      
      // Приглушенные темные цвета для заливки (уменьшаем яркость)
      const r = Math.floor((15 + gradient * 100) * 0.4);  // Темнее
      const g = Math.floor((40 + gradient * 120) * 0.4);  // Темнее
      const b = Math.floor((80 + gradient * 140) * 0.5);  // Темнее, но синий немного ярче
      
      return { r, g, b };
    };

    // Функция для получения современного градиентного цвета для граней
    const getGradientColorForEdge = (x1: number, y1: number, x2: number, y2: number, t: number): { r: number; g: number; b: number } => {
      // Современный градиент: Голубой -> Синий -> Фиолетовый -> Розовый
      // t - позиция вдоль линии (0-1)
      
      // Цвета градиента (RGB)
      const colors = [
        { r: 0, g: 212, b: 255 },    // Голубой (#00D4FF)
        { r: 0, g: 102, b: 255 },    // Синий (#0066FF)
        { r: 139, g: 92, b: 246 },   // Фиолетовый (#8B5CF6)
        { r: 236, g: 72, b: 153 }    // Розовый (#EC4899)
      ];
      
      // Определяем, между какими цветами интерполировать
      const segment = t * (colors.length - 1);
      const index = Math.floor(segment);
      const localT = segment - index;
      
      // Интерполируем между двумя соседними цветами
      const color1 = colors[Math.min(index, colors.length - 1)];
      const color2 = colors[Math.min(index + 1, colors.length - 1)];
      
      // Плавная интерполяция с использованием ease-in-out функции
      const smoothT = localT * localT * (3 - 2 * localT); // Smoothstep
      
      const r = Math.floor(color1.r + smoothT * (color2.r - color1.r));
      const g = Math.floor(color1.g + smoothT * (color2.g - color1.g));
      const b = Math.floor(color1.b + smoothT * (color2.b - color1.b));
      
      return { r, g, b };
    };

    // Функция для получения цвета на основе позиции точки (для точек)
    const getColorForPoint = (x: number, y: number): string => {
      const { r, g, b } = getRGBForPoint(x, y);
      const normalizedX = x / canvas.width;
      const normalizedY = y / canvas.height;
      const gradient = normalizedX * 0.5 + normalizedY * 0.5;
      const alpha = 0.25 + gradient * 0.5;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Функция для создания треугольников из точек (упрощенная триангуляция)
    const createTriangles = (points: Point[]): Triangle[] => {
      const triangles: Triangle[] = [];
      const processedTriangles = new Set<string>();
      
      // Для каждой точки находим соседей и создаем треугольники
      for (let i = 0; i < points.length; i++) {
        const p1 = points[i];
        const neighbors: Array<{ index: number; distance: number }> = [];
        
        // Находим все соседние точки в пределах connectionDistance
        for (let j = 0; j < points.length; j++) {
          if (i !== j) {
            const dist = distance(p1, points[j]);
            if (dist < connectionDistance) {
              neighbors.push({ index: j, distance: dist });
            }
          }
        }
        
        // Сортируем соседей по расстоянию
        neighbors.sort((a, b) => a.distance - b.distance);
        
        // Создаем треугольники с ближайшими соседями
        for (let n1 = 0; n1 < Math.min(neighbors.length, 5); n1++) {
          for (let n2 = n1 + 1; n2 < Math.min(neighbors.length, 5); n2++) {
            const p2 = points[neighbors[n1].index];
            const p3 = points[neighbors[n2].index];
            
            // Проверяем, что все стороны треугольника в пределах connectionDistance
            const d12 = neighbors[n1].distance;
            const d13 = distance(p1, p3);
            const d23 = distance(p2, p3);
            
            if (d12 < connectionDistance && d13 < connectionDistance && d23 < connectionDistance) {
              // Создаем уникальный ключ для треугольника
              const trianglePoints = [
                [i, neighbors[n1].index, neighbors[n2].index]
              ].map(indices => indices.sort((a, b) => a - b).join(','));
              const key = trianglePoints[0];
              
              if (!processedTriangles.has(key)) {
                processedTriangles.add(key);
                
                // Вычисляем центр треугольника для определения цвета
                const centerX = (p1.x + p2.x + p3.x) / 3;
                const centerY = (p1.y + p2.y + p3.y) / 3;
                const rgb = getRGBForPoint(centerX, centerY);
                
                // Вычисляем среднее расстояние между точками треугольника
                const avgDistance = (d12 + d13 + d23) / 3;
                
                // Плавная интерполяция прозрачности на основе расстояния
                // Чем ближе точки, тем более непрозрачный треугольник
                // Используем функцию ease-out для плавного перехода
                const normalizedDistance = avgDistance / connectionDistance; // 0-1
                
                // Квадратичная функция для плавного появления/исчезновения
                // Когда normalizedDistance = 0 (точки очень близко), alpha = 1
                // Когда normalizedDistance = 1 (точки на границе), alpha = 0
                const smoothFactor = 1 - Math.pow(normalizedDistance, 1.5);
                
                // Минимальная и максимальная прозрачность
                const minAlpha = 0.1;  // Минимальная видимость даже для дальних треугольников
                const maxAlpha = 0.75;  // Максимальная непрозрачность для близких треугольников
                const alpha = minAlpha + smoothFactor * (maxAlpha - minAlpha);
                
                const color = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
                
                triangles.push({ p1, p2, p3, color, alpha });
              }
            }
          }
        }
      }
      
      return triangles;
    };

    // Анимация
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Обновление позиций точек
      const points = pointsRef.current;
      for (let i = 0; i < points.length; i++) {
        const point = points[i];
        
        // Обновляем позицию
        point.x += point.vx;
        point.y += point.vy;
        
        // Отскок от краев
        if (point.x < 0 || point.x > canvas.width) {
          point.vx = -point.vx;
          point.x = Math.max(0, Math.min(canvas.width, point.x));
        }
        if (point.y < 0 || point.y > canvas.height) {
          point.vy = -point.vy;
          point.y = Math.max(0, Math.min(canvas.height, point.y));
        }
        
        // Небольшое случайное изменение скорости для более естественного движения
        if (Math.random() < 0.01) {
          point.vx += (Math.random() - 0.5) * 0.1;
          point.vy += (Math.random() - 0.5) * 0.1;
          
          // Ограничиваем скорость
          const maxSpeed = pointSpeed * 2;
          point.vx = Math.max(-maxSpeed, Math.min(maxSpeed, point.vx));
          point.vy = Math.max(-maxSpeed, Math.min(maxSpeed, point.vy));
        }
      }

      // Создаем треугольники
      trianglesRef.current = createTriangles(points);

      // Рисуем треугольники
      for (const triangle of trianglesRef.current) {
        // Рисуем заливку (приглушенную и темную)
        ctx.beginPath();
        ctx.moveTo(triangle.p1.x, triangle.p1.y);
        ctx.lineTo(triangle.p2.x, triangle.p2.y);
        ctx.lineTo(triangle.p3.x, triangle.p3.y);
        ctx.closePath();
        ctx.fillStyle = triangle.color;
        ctx.fill();
        
        // Рисуем грани с современным градиентом
        const strokeAlpha = Math.min(0.8, 0.3 + triangle.alpha * 0.7);
        ctx.lineWidth = 1.2;
        
        // Рисуем каждую грань с градиентом
        const edges = [
          { start: triangle.p1, end: triangle.p2 },
          { start: triangle.p2, end: triangle.p3 },
          { start: triangle.p3, end: triangle.p1 }
        ];
        
        for (const edge of edges) {
          const start = edge.start;
          const end = edge.end;
          
          // Создаем линейный градиент для этой грани
          const gradient = ctx.createLinearGradient(start.x, start.y, end.x, end.y);
          
          // Добавляем цветовые остановки для современного градиента
          const color1 = getGradientColorForEdge(start.x, start.y, end.x, end.y, 0);
          const color2 = getGradientColorForEdge(start.x, start.y, end.x, end.y, 0.5);
          const color3 = getGradientColorForEdge(start.x, start.y, end.x, end.y, 1);
          
          gradient.addColorStop(0, `rgba(${color1.r}, ${color1.g}, ${color1.b}, ${strokeAlpha})`);
          gradient.addColorStop(0.5, `rgba(${color2.r}, ${color2.g}, ${color2.b}, ${strokeAlpha})`);
          gradient.addColorStop(1, `rgba(${color3.r}, ${color3.g}, ${color3.b}, ${strokeAlpha})`);
          
          ctx.beginPath();
          ctx.moveTo(start.x, start.y);
          ctx.lineTo(end.x, end.y);
          ctx.strokeStyle = gradient;
          ctx.stroke();
        }
      }

      // Рисуем точки
      for (const point of points) {
        ctx.beginPath();
        ctx.arc(point.x, point.y, pointRadius, 0, Math.PI * 2);
        ctx.fillStyle = getColorForPoint(point.x, point.y);
        ctx.fill();
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
};

export default AnimatedTrianglesBackground;

